/**
 * Drives the Wall Breaker (slingshot) screen. The physics + input live inside a WebView game runtime; this
 * hook owns the economy bridge: it boots the game with the day's config on `ready`, reports each cleared
 * level to the server, and syncs coins from the SERVER response only (never a client-computed total). The
 * server decides every award + the daily cap, so a spoofed clear can't mint coins.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useInvalidateXpSummary } from '../../economy/hooks/useXpSummary';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics/events';
import { startSlingshotRun, recordSlingshotLevelClear } from '../helpers/slingshotApi';
import { buildSlingshotGameHtml } from '../helpers/slingshotGameHtml';
import {
  SLINGSHOT_GAME_KEY,
  SLINGSHOT_SESSION_COIN_CAP,
  SLINGSHOT_LEVEL_COUNT,
  SLINGSHOT_BALLS_PER_LEVEL,
  type SlingshotBridgeMessage,
} from '../constants/slingshot.constants';

type Status = 'loading' | 'ready' | 'error';
const TOAST_MS = 2200;

/** A milestone celebration — Wall Breaker fires one on every 10th clear; every 20th is "major". */
export interface SlingshotMilestone {
  level: number;
  tier: 'minor' | 'major';
}
/** Milestone for a cleared level, or null. The final level (`SLINGSHOT_LEVEL_COUNT`) is owned by the
 *  campaign-complete overlay, so it never fires a milestone. */
function milestoneFor(level: number): SlingshotMilestone | null {
  if (level % 10 !== 0 || level >= SLINGSHOT_LEVEL_COUNT) return null;
  return { level, tier: level % 20 === 0 ? 'major' : 'minor' };
}

/** Day config pushed into the game runtime once the economy state is known. */
export interface SlingshotStartConfig {
  levelCount: number;
  ballsPerLevel: number;
  startLevel: number;
}

/**
 * Intent-based host surface (not raw JS injection) so the same hook drives both the native WebView and the
 * web iframe, and stays unit-testable without either. The platform host components implement this.
 */
export interface SlingshotWebViewRef {
  applyStart: (config: SlingshotStartConfig) => void;
  reload: () => void;
}

export function useSlingshotGame() {
  const apiClient = useApiClient();
  const syncCoins = useEconomyStore((s) => s.syncCoins);
  const invalidateXp = useInvalidateXpSummary();
  const { track } = useAnalytics();

  const html = useMemo(() => buildSlingshotGameHtml(), []);
  const webViewRef = useRef<SlingshotWebViewRef | null>(null);
  const startedTrackedRef = useRef(false);

  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [coinsLeftToday, setCoinsLeftToday] = useState(SLINGSHOT_SESSION_COIN_CAP);
  const [capReached, setCapReached] = useState(false);
  const [bestLevel, setBestLevel] = useState(0);
  const [coinsBeat, setCoinsBeat] = useState(0); // the "+N" that floats up on a paid clear
  const [newBest, setNewBest] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [finalLevel, setFinalLevel] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  // Session tally (this play, not all-time) — feeds the exit summary dialog.
  const [levelReached, setLevelReached] = useState(0);
  const [levelsCleared, setLevelsCleared] = useState(0);
  const [sessionCoins, setSessionCoins] = useState(0);
  const [showExitSummary, setShowExitSummary] = useState(false);
  const [milestone, setMilestone] = useState<SlingshotMilestone | null>(null);

  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const coinBeatTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    if (coinBeatTimer.current) clearTimeout(coinBeatTimer.current);
  }, []);
  const flashToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const bindWebView = useCallback((ref: SlingshotWebViewRef | null) => { webViewRef.current = ref; }, []);

  /** Fetch today's economy state and boot the game runtime with it. Called once the WebView is `ready`. */
  const start = useCallback(async () => {
    setStatus('loading');
    setErrorMessage(null);
    try {
      const cfg = await startSlingshotRun(apiClient);
      setCoinsLeftToday(cfg.coinsLeftToday);
      setCapReached(cfg.capReached);
      setBestLevel(cfg.bestLevel);
      setStatus('ready');
      webViewRef.current?.applyStart({ levelCount: cfg.levelCount, ballsPerLevel: cfg.ballsPerLevel, startLevel: 1 });
    } catch {
      // Offline fallback — boot with defaults so the game is always playable (coins won't bank)
      setStatus('ready');
      webViewRef.current?.applyStart({ levelCount: SLINGSHOT_LEVEL_COUNT, ballsPerLevel: SLINGSHOT_BALLS_PER_LEVEL, startLevel: 1 });
    }
  }, [apiClient]);

  const handleClear = useCallback(async (msg: Extract<SlingshotBridgeMessage, { type: 'level-clear' }>) => {
    let coinsAwarded = 0; // stays 0 if the server call fails (offline / staging without routes)
    try {
      const res = await recordSlingshotLevelClear(apiClient, { level: msg.level, ballsLeft: msg.ballsLeft, stars: msg.stars });
      coinsAwarded = res.coinsAwarded;
      // Coins come from the server only — anonymous users get no `coins`, so nothing is synced.
      if (typeof res.coins === 'number') syncCoins(res.coins);
      invalidateXp(); // completion earns XP even when coins are daily-capped
      setCoinsBeat(res.coinsAwarded);
      if (coinBeatTimer.current) clearTimeout(coinBeatTimer.current);
      coinBeatTimer.current = setTimeout(() => setCoinsBeat(0), 1500);
      setCoinsLeftToday(res.coinsLeftToday);
      setCapReached(res.capReached);
      setBestLevel(res.bestLevel);
      setNewBest(res.newBest);
    } catch (err) {
      // Gameplay never blocks on the network — the level already advanced in the WebView; just note it.
      flashToast(extractErrorMessage(err, 'Coins will catch up'));
    } finally {
      // Session tally for the exit summary — counts even offline (coinsAwarded is 0 when the server failed).
      setLevelsCleared((n) => n + 1);
      setSessionCoins((c) => c + coinsAwarded);
      setLevelReached((lv) => Math.max(lv, msg.level));
      // Progression signal — fire regardless of the server result so the drop-off funnel stays accurate.
      track(ANALYTICS_EVENTS.GAME_LEVEL_CLEARED, {
        game_key: SLINGSHOT_GAME_KEY,
        level: msg.level,
        stars: msg.stars,
        balls_left: msg.ballsLeft,
        coins_awarded: coinsAwarded,
      });
      // Every 10th clear pops a celebratory milestone (the WebView keeps advancing underneath).
      const ms = milestoneFor(msg.level);
      if (ms) {
        setMilestone(ms);
        track(ANALYTICS_EVENTS.GAME_MILESTONE_REACHED, {
          game_key: SLINGSHOT_GAME_KEY,
          level: ms.level,
          coins_awarded: coinsAwarded,
          reason: ms.tier,
        });
      }
    }
  }, [apiClient, syncCoins, invalidateXp, flashToast, track]);

  const onMessage = useCallback((event: { nativeEvent: { data: string } }) => {
    let msg: SlingshotBridgeMessage | { type: 'load-error' };
    try { msg = JSON.parse(event.nativeEvent.data); } catch { return; }
    switch (msg.type) {
      case 'ready':
        if (!startedTrackedRef.current) {
          startedTrackedRef.current = true;
          track(ANALYTICS_EVENTS.GAME_STARTED, { game_key: SLINGSHOT_GAME_KEY });
        }
        void start();
        break;
      case 'level-clear':
        void handleClear(msg);
        break;
      case 'complete':
        setFinalLevel(msg.level);
        setLevelReached((lv) => Math.max(lv, msg.level));
        setCompleted(true);
        track(ANALYTICS_EVENTS.GAME_COMPLETED, { game_key: SLINGSHOT_GAME_KEY, level: msg.level, reason: 'campaign' });
        break;
      case 'level-failed':
        // Drop-off / rage-quit signal — the WebView also shows its own retry toast.
        setLevelReached((lv) => Math.max(lv, msg.level));
        track(ANALYTICS_EVENTS.GAME_LEVEL_FAILED, { game_key: SLINGSHOT_GAME_KEY, level: msg.level });
        break;
      case 'load-error':
        setErrorMessage('Could not load the game');
        setStatus('error');
        break;
      default:
        break;
    }
  }, [start, handleClear, track]);

  const retry = useCallback(() => {
    setStatus('loading');
    setErrorMessage(null);
    webViewRef.current?.reload(); // WebView re-posts `ready` → start() runs again
  }, []);

  const onPlayAgain = useCallback(() => {
    setCompleted(false);
    setFinalLevel(0);
    webViewRef.current?.reload();
  }, []);

  /** Dismiss the milestone celebration and return to the game (already on the next level underneath). */
  const dismissMilestone = useCallback(() => setMilestone(null), []);

  /** Back pressed mid-game → open the session summary instead of leaving straight away. */
  const requestExit = useCallback(() => setShowExitSummary(true), []);
  const dismissExitSummary = useCallback(() => setShowExitSummary(false), []);
  /** User confirmed "Leave" from the summary — close the session funnel, then the view navigates away. */
  const confirmExit = useCallback(() => {
    track(ANALYTICS_EVENTS.GAME_COMPLETED, {
      game_key: SLINGSHOT_GAME_KEY,
      level: levelReached,
      coins_awarded: sessionCoins,
      reason: 'exit',
    });
  }, [track, levelReached, sessionCoins]);

  return {
    status,
    isLoading: status === 'loading',
    isError: status === 'error',
    errorMessage,
    html,
    coinsLeftToday,
    capReached,
    bestLevel,
    coinsBeat,
    newBest,
    completed,
    finalLevel,
    toast,
    levelReached,
    levelsCleared,
    sessionCoins,
    showExitSummary,
    milestone,
    bindWebView,
    onMessage,
    retry,
    onPlayAgain,
    requestExit,
    dismissExitSummary,
    confirmExit,
    dismissMilestone,
  };
}

export type UseSlingshotGame = ReturnType<typeof useSlingshotGame>;
